import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AnomalyDetectResult } from '@/lib/insightsApi';

interface Props {
  data?: AnomalyDetectResult;
  isLoading?: boolean;
}

const SEVERITY_STYLE: Record<string, string> = {
  high: 'bg-red-500 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-blue-500 text-white',
};

export function AnomalyAlertBadge({ data, isLoading }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (isLoading || !data?.detected || data.anomalies.length === 0) return null;

  const highCount = data.anomalies.filter((a) => a.severity === 'high').length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition',
          highCount > 0
            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-200',
        )}
      >
        <AlertTriangle size={12} />
        {t('insights.anomaly.badge', { count: data.anomalies.length, defaultValue: `${data.anomalies.length} anomalie` })}
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-50 w-72 card shadow-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold dark:text-white">
              {t('insights.anomaly.title')}
            </p>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          </div>
          <ul className="space-y-2">
            {data.anomalies.map((a, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={cn('mt-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded shrink-0', SEVERITY_STYLE[a.severity] ?? SEVERITY_STYLE.low)}>
                  {a.severity.toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium dark:text-white capitalize">
                    {a.metric.replace(/_/g, ' ')} {a.direction === 'up' ? '↑' : '↓'}
                  </p>
                  <p className="text-[11px] text-slate-500">{a.description}</p>
                  <p className="text-[10px] text-slate-400 font-mono">z={a.z_score.toFixed(2)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
